import { ICloneable } from '../../contracts/cloneable.js';
import { IDataComparable } from '../../contracts/data-comparable.js';
import { MapUtils } from '../../utility/map-utils.js';
import { Language } from './language.js';
import {
  DataModel, DataModelBuilder, SerializationContext, SerializedData, cloneList, cloneListOfCloneable,
  cloneMap, convertFromLanguageMap, convertList, convertToLanguageMap, isSameData, isSameList, isSameMap,
} from '../dto/dto.js';
import { VersionRange } from '../core/version-range.js';
import { isNullOrUndefined } from '../../utility/null-safety.js';

export interface DiscoverMetadata {
  uniqueId: string,
  webRoute: string,
  isFestival: boolean,
  referralLink: string | null,
  images: string[],
  title: Map<Language, string>,
  description: Map<Language, string>,
  location: Map<Language, string>,
  seoTags: { title: string, description: string, url: string | null, image: string | null },
}

export class DiscoverSEO implements IDataComparable, ICloneable {
  private constructor(
    public readonly title: string,
    public readonly description: string,
    public readonly url: string | null,
    public readonly image: string | null,
  ) {}

  static fromData(args: {
    title: string,
    description: string,
    url: string | null,
    image: string | null,
  }): DiscoverSEO {
    return new DiscoverSEO(args.title, args.description, args.url, args.image);
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverSEO {
    return new DiscoverSEO(
        MapUtils.get<string>(map, 'title'),
        MapUtils.get<string>(map, 'description'),
        MapUtils.getOrNull<string>(map, 'url'),
        MapUtils.getOrNull<string>(map, 'image'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    const map: SerializedData = {
      'title': this.title,
      'description': this.description,
    };

    MapUtils.setOptional(map, 'url', this.url);
    MapUtils.setOptional(map, 'image', this.image);
    return map;
  }

  clone(): this {
    return new DiscoverSEO(
        this.title,
        this.description,
        this.url,
        this.image,
    ) as this;
  }

  isSame(other: DiscoverSEO): boolean {
    if(this === other) {
      return true;
    }

    return other.title === this.title &&
          other.description === this.description &&
          other.url === this.url &&
          other.image === this.image;
  }
}

export class DiscoverCategory implements IDataComparable, ICloneable {
  private constructor(
    public readonly uniqueId: string,
    public readonly icon: string | null,
    public readonly name: Map<Language, string>,
    public readonly isFeatured: boolean,
    public readonly rank: number,
    public readonly filterOrder: number,
  ) {}

  static fromData(args: {
    uniqueId: string,
    icon: string | null,
    name: Map<Language, string>,
    isFeatured: boolean,
    rank: number,
    filterOrder: number,
  }): DiscoverCategory {
    return new DiscoverCategory(args.uniqueId, args.icon, args.name, args.isFeatured, args.rank, args.filterOrder);
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverCategory {
    return new DiscoverCategory(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'icon'),
        convertToLanguageMap(map, 'name'),
        MapUtils.get<boolean>(map, 'isFeatured'),
        MapUtils.get<number>(map, 'rank'),
        MapUtils.get<number>(map, 'filterOrder'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    const map: SerializedData = {
      'uniqueId': this.uniqueId,
      'name': convertFromLanguageMap(this.name),
      'isFeatured': this.isFeatured,
      'rank': this.rank,
      'filterOrder': this.filterOrder,
    };

    MapUtils.setOptional(map, 'icon', this.icon);
    return map;
  }

  clone(): this {
    return new DiscoverCategory(
        this.uniqueId,
        this.icon,
        cloneMap(this.name),
        this.isFeatured,
        this.rank,
        this.filterOrder,
    ) as this;
  }

  isSame(other: DiscoverCategory): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
          other.icon === this.icon &&
          other.isFeatured === this.isFeatured &&
          other.rank === this.rank &&
          other.filterOrder === this.filterOrder &&
          isSameMap(other.name, this.name);
  }
}

export class DiscoverItem implements IDataComparable, ICloneable {
  private constructor(
    public readonly uniqueId: string,
    public readonly category: string,
      // public readonly name: Map<Language, string>,
      // public readonly cover: string,
      // public readonly locality: string,
      // public readonly localityId: string,
      // public readonly classification: BusinessCategory,
      // public readonly badgeType?: SubscriptionBadgeType,
      // public readonly hasMenu?: boolean,
      // public readonly hasDrinks?: boolean,
  ) {}

  static fromData(args: { uniqueId: string, category: string }): DiscoverItem {
    return new DiscoverItem(
        args.uniqueId,
        args.category,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverItem {
    return new DiscoverItem(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'category'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'category': this.category,
    };
  }

  clone(): this {
    return new DiscoverItem(
        this.uniqueId,
        this.category,
    ) as this;
  }

  isSame(other: DiscoverItem): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.category === this.category;
  }
}

export class DiscoverContent extends DataModel {
  static readonly TYPE_ID: string = '138fb56544c541b98549755f6e00b129';
  static readonly SCHEMA_VERSION: number = 3;

  private constructor(
    public readonly uniqueId: string,
    public readonly webRoute: string,
    public readonly isFestival: boolean,
    public readonly referralLink: string | null,
    public readonly images: string[],
    public readonly title: Map<Language, string>,
    public readonly description: Map<Language, string>,
    public readonly location: Map<Language, string>,
    public readonly categories: DiscoverCategory[],
    public readonly items: DiscoverItem[],
    public readonly seoTags: DiscoverSEO | null,
  ) {
    super();
  }

  get $typeId() {
    return DiscoverContent.TYPE_ID;
  }

  get $schemaVersion(): VersionRange {
    return VersionRange.between(1, DiscoverContent.SCHEMA_VERSION);
  }

  static fromData(opt: {
    metadata: DiscoverMetadata,
    categories: DiscoverCategory[],
    items: DiscoverItem[],
  }): DiscoverContent {
    return new DiscoverContent(
        opt.metadata.uniqueId,
        opt.metadata.webRoute,
        opt.metadata.isFestival,
        opt.metadata.referralLink,
        opt.metadata.images,
        opt.metadata.title,
        opt.metadata.description,
        opt.metadata.location,
        opt.categories,
        opt.items,
        DiscoverSEO.fromData(opt.metadata.seoTags),
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): DiscoverContent {
    const seoData = MapUtils.getOrNull<SerializedData>(map, 'seoTags');

    return new DiscoverContent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.get<string>(map, 'webRoute'),
        MapUtils.get<boolean>(map, 'isFestival'),
        MapUtils.getOrNull<string>(map, 'referralLink'),
        MapUtils.getList<string>(map, 'images', (v) => v as string),
        convertToLanguageMap(map, 'title'),
        convertToLanguageMap(map, 'description'),
        convertToLanguageMap(map, 'location'),
        MapUtils.getList<DiscoverCategory>(map, 'categories', (v) => {
          return DiscoverCategory.fromMap(context, v as SerializedData);
        }),
        MapUtils.getList<DiscoverItem>(map, 'items', (v) => {
          return DiscoverItem.fromMap(context, v as SerializedData);
        }),
        isNullOrUndefined(seoData) ? null : DiscoverSEO.fromMap(context, seoData),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'webRoute': this.webRoute,
      'isFestival': this.isFestival,
      'referralLink': this.referralLink,
      'images': this.images,
      'title': convertFromLanguageMap(this.title),
      'description': convertFromLanguageMap(this.description),
      'location': convertFromLanguageMap(this.location),
      'categories': convertList(this.categories, (e) => e.toMap(context)),
      'items': convertList(this.items, (e) => e.toMap(context)),
      'seoTags': this.seoTags?.toMap(context) ?? null,
    };
  }

  clone(): this {
    return new DiscoverContent(
        this.uniqueId,
        this.webRoute,
        this.isFestival,
        this.referralLink,
        cloneList(this.images),
        cloneMap(this.title),
        cloneMap(this.description),
        cloneMap(this.location),
        cloneListOfCloneable(this.categories),
        cloneListOfCloneable(this.items),
        this.seoTags?.clone() ?? null,
    ) as this;
  }

  isSame(other: DiscoverContent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
          other.webRoute === this.webRoute &&
          other.isFestival === this.isFestival &&
          isSameData(other.seoTags, this.seoTags) &&
          isSameList(other.images, this.images) &&
          isSameMap(other.title, this.title) &&
          isSameMap(other.description, this.description) &&
          isSameMap(other.location, this.location) &&
          isSameList(other.categories, this.categories, isSameData) &&
          isSameList(other.items, this.items, isSameData);
  }
}

export class DiscoverContentBuilder extends DataModelBuilder<DiscoverContent> {
  build(context: SerializationContext, map: SerializedData): DiscoverContent {
    return DiscoverContent.fromMap(context, map);
  }

  migrateToCurrentSchemaVersion(context: SerializationContext, map: SerializedData): SerializedData | null {
    const versionRange = MapUtils.getVersionRange(map);
    if(versionRange.contains(DiscoverContent.SCHEMA_VERSION)) {
      return map;
    }
    if(versionRange.isGreaterThan(DiscoverContent.SCHEMA_VERSION)) {
      return null; // This data comes from the FUTURE. We have no idea how to migrate it.
    }

    let version = versionRange.to;

    // In this version `seoTags` of type [DiscoverSEO] was introduced.
    if(version < 2) {
      map['seoTags'] = null;
      version = 2;
    }

    // In this version:
    //  - `referralLink` of type [string] was introduced.
    //  - `location` of type [Map<Language, string>] was introduced.
    if(version < 3) {
      map['referralLink'] = null;
      map['location'] = {};
      version = 3;
    }

    map['$schema_version'] = VersionRange.encodedExactly(version);
    return map;
  }
}
